/** Editor для Специфікації номенклатури (Порція 4b).
 *
 * Особливість: довідник, не документ. Немає posted/unpost; лише save/delete.
 * One-active rule перевіряється backend → 400 з detail (показуємо у error div).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import {
  nomenclatureSpecifications,
  type NomenclatureSpecificationItemIn,
  type SpecificationPurpose,
} from '@/api/nomenclatureSpecifications';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { usePermissions } from '@/auth/usePermissions';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import type { GoodsBalanceRow } from '@/api/documents';
import { refs as refsApi } from '@/api/refs';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import TchTable from '@/shared/components/TchTable';

const PERMISSION = 'production.specification.manage';

interface FormState {
  organization_id: string;
  product_nomenclature_id: string;
  base_quantity: string;
  name: string;
  description: string;
  purpose: SpecificationPurpose;
  is_active: boolean;
  items: NomenclatureSpecificationItemIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  product_nomenclature_id: '',
  base_quantity: '1',
  name: '',
  description: '',
  purpose: 'PRODUCTION',
  is_active: true,
  items: [],
};

const sumPct = (items: NomenclatureSpecificationItemIn[]): number =>
  items.reduce((acc, it) => acc + (it.cost_percentage == null || it.cost_percentage === '' ? 0 : Number(it.cost_percentage)), 0);

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function NomenclatureSpecificationEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';
  // Префіл з закладки «Комплектація» картки: ?product=<nomId>&purpose=ASSEMBLY.
  const prefillProduct = searchParams.get('product');
  const prefillPurpose = searchParams.get('purpose') as SpecificationPurpose | null;

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const { has } = usePermissions();
  const canManage = has(PERMISSION);
  // ч.653: автоназва «Основна — <продукція>» (як 1С заповнює Наименование).
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.listAll() });

  const docQuery = useQuery({
    queryKey: ['nomenclature-specification', id],
    queryFn: () => nomenclatureSpecifications.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [pickerOpen, setPickerOpen] = useState(false);
  // ч.657: одиниці виміру для колонки «Одиниця».
  const uomsQ = useQuery({ queryKey: ['uoms-for-picker'], queryFn: refsApi.uoms });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({
        ...f,
        organization_id: availableOrgs[0]!.id,
        product_nomenclature_id: prefillProduct ?? f.product_nomenclature_id,
        purpose: prefillPurpose ?? f.purpose,
      }));
    }
  }, [isNew, availableOrgs, form.organization_id, prefillProduct, prefillPurpose]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        product_nomenclature_id: d.product_nomenclature_id,
        base_quantity: d.base_quantity,
        name: d.name,
        description: d.description ?? '',
        purpose: d.purpose ?? 'PRODUCTION',
        is_active: d.is_active,
        items: d.items.map((it) => ({
          line_number: it.line_number,
          raw_material_nomenclature_id: it.raw_material_nomenclature_id,
          quantity: it.quantity,
          uom_id: it.uom_id,
          comment: it.comment,
          cost_percentage: it.cost_percentage,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const create = useMutation({
    mutationFn: () =>
      nomenclatureSpecifications.create({
        organization_id: form.organization_id,
        product_nomenclature_id: form.product_nomenclature_id,
        base_quantity: form.base_quantity,
        name: form.name,
        description: form.description || null,
        purpose: form.purpose,
        is_active: form.is_active,
        items: form.items,
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['nomenclature-specifications'] });
      navigate(`/nomenclature-specifications/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  const update = useMutation({
    mutationFn: () =>
      nomenclatureSpecifications.update(id!, {
        product_nomenclature_id: form.product_nomenclature_id,
        base_quantity: form.base_quantity,
        name: form.name,
        description: form.description || null,
        purpose: form.purpose,
        is_active: form.is_active,
        items: form.items,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nomenclature-specification', id] });
      qc.invalidateQueries({ queryKey: ['nomenclature-specifications'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: () => nomenclatureSpecifications.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nomenclature-specifications'] });
      navigate('/nomenclature-specifications');
    },
    onError: (e) => setError(extractError(e)),
  });

  const uomShortById = useMemo(() => {
    const m = new Map<string, string>();
    (uomsQ.data ?? []).forEach((u) => m.set(u.id, u.short_name));
    return m;
  }, [uomsQ.data]);
  const uomOfRow = (it: NomenclatureSpecificationItemIn): string => {
    if (it.uom_id) return uomShortById.get(it.uom_id) ?? '';
    const n = nomQ.data?.items.find((x) => x.id === it.raw_material_nomenclature_id);
    return (n?.base_uom_id && uomShortById.get(n.base_uom_id)) || '';
  };
  // ч.657/658: підбір компонентів з попапу (канон реалізації, МУЛЬТИВИБІР).
  function addRowsFromPicker(rows: GoodsBalanceRow[]) {
    setForm((f) => ({
      ...f,
      items: [...f.items, ...rows.map((row, k) => {
        return {
          line_number: f.items.length + 1 + k,
          raw_material_nomenclature_id: row.nomenclature_id,
          quantity: '1',
          // uom_id НЕ підставляємо: поле — FK на ref.nomenclature_uoms
          // (одиниці КОНКРЕТНОЇ номенклатури), а base_uom_id — класифікатор.
          // NULL = базова одиниця (конвенція); колонка «Одиниця» показує її
          // з картки. Прецедент-фікс: FK violation при збереженні (ч.671).
          uom_id: null,
        };
      })],
    }));
  }
  function removeItem(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })),
    }));
  }
  function setItemField(i: number, patch: Partial<NomenclatureSpecificationItemIn>) {
    setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  const isAssembly = form.purpose === 'ASSEMBLY';
  const total = sumPct(form.items);
  // Для ASSEMBLY сума долей має = 100 (з допуском на похибку відображення).
  const pctOk = !isAssembly || (form.items.length > 0 && Math.abs(total - 100) < 0.005);
  const saveDisabled = create.isPending || update.isPending || (isAssembly && !pctOk);

  // ч.911 (новий дизайн, Закон #10): «ще дій» у картки не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  // ч.911: прогрес — ЛИШЕ обовʼязкові поля (довідник: блоки без номерів).
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 3,
      filled: (form.organization_id ? 1 : 0) + (form.product_nomenclature_id ? 1 : 0)
        + (form.name ? 1 : 0) },
    { id: 'items', title: 'Склад специфікації', required: 1,
      filled: form.items.length > 0 ? 1 : 0 },
  ];

  return (
    <div className="card">
      {/* ч.911 (новий дизайн, Закон #10): блоки + права рейка (картка довідника). */}
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова специфікація' : `Специфікація: ${form.name}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/nomenclature-specifications')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          saveDisabled={!canManage || saveDisabled}
          isNew={isNew}
          postable={false}
          hidePostedBadge
          extraStatusBadges={!isNew && !form.is_active ? [{ label: 'неактивна', tone: 'gray' }] : []}
          deletable={canManage}
          onDelete={() => remove.mutate()}
          deletePending={remove.isPending}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew} related={false}
          doc={{ type: 'ref.nomenclature_specifications', id: isNew ? '' : id! }} />
      }>
      {/* ч.655 (розкладка власника): [Юрособа* | Вихідна продукція* | Назва*]
          → [Призначення | Базова к-сть | Активна] → Опис. ч.911 — блоками. */}
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація <span style={{ color: '#dc2626' }}>*</span></label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>Вихідна продукція <span style={{ color: '#dc2626' }}>*</span></label>
          <NomenclatureSelect value={form.product_nomenclature_id}
            onChange={(v) => {
              const prod = nomQ.data?.items.find((n) => n.id === v);
              const auto = prod ? `Основна — ${prod.name}` : '';
              setForm((f) => ({
                ...f, product_nomenclature_id: v,
                // Автоназва, поки користувач не вписав власну.
                name: (!f.name || f.name.startsWith('Основна — ')) && auto ? auto : f.name,
              }));
            }} kind="goods" />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Назва <span style={{ color: '#dc2626' }}>*</span></label>
          <input value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
      </div>
      <div className="doc-grid" style={{ alignItems: 'flex-end' }}>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Призначення</label>
          <select value={form.purpose}
                  onChange={(e) => setForm({ ...form, purpose: e.target.value as SpecificationPurpose })}>
            <option value="PRODUCTION">Виробництво (рецептура)</option>
            <option value="ASSEMBLY">Набір (комплект)</option>
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Базова кількість</label>
          <input type="number" step="0.001" value={form.base_quantity}
                 onChange={(e) => setForm({ ...form, base_quantity: e.target.value })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 7', paddingBottom: '0.35rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_active}
                   onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Активна
          </label>
        </div>
      </div>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 12' }}>
          <label>Опис</label>
          <input value={form.description}
                 onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </div>
      </DocBlock>

      <DocBlock index={2} title="Склад специфікації"
        actions={(
          <button type="button" className="primary" onClick={() => setPickerOpen(true)}
            disabled={!form.organization_id}
            title={!form.organization_id ? 'Спершу оберіть юрособу' : 'Підбір з довідника номенклатури'}>
            + додати номенклатуру
          </button>
        )}>
      <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
        {isAssembly
          ? 'Склад набору (компоненти + долі собівартості)'
          : `Склад сировини на ${form.base_quantity} ${form.base_quantity === '1' ? 'одиницю' : 'одиниць'}`}
      </div>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>{isAssembly ? 'Компонент' : 'Сировина'}</th>
            <th style={{ width: 100 }}>К-сть</th>
            <th style={{ width: 90 }}>Одиниця</th>
            {isAssembly && <th style={{ width: 110 }}>Доля %</th>}
            <th>Примітка</th>
            <th style={{ width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i}>
              <td>{it.line_number}</td>
              <td>
                <NomenclatureSelect value={it.raw_material_nomenclature_id}
                  onChange={(v) => setItemField(i, { raw_material_nomenclature_id: v })} kind="goods" />
              </td>
              <td>
                <input type="number" step="0.001" value={it.quantity}
                       onChange={(e) => setItemField(i, { quantity: e.target.value })} />
              </td>
              <td style={{ color: 'var(--muted)' }}>{uomOfRow(it)}</td>
              {isAssembly && (
                <td>
                  <input type="number" step="0.01" min="0" max="100"
                         value={it.cost_percentage ?? ''}
                         placeholder="%"
                         onChange={(e) => setItemField(i, { cost_percentage: e.target.value || null })} />
                </td>
              )}
              <td>
                <input value={it.comment ?? ''}
                       onChange={(e) => setItemField(i, { comment: e.target.value })} />
              </td>
              <td>
                <button onClick={() => removeItem(i)} title="Видалити">×</button>
              </td>
            </tr>
          ))}
        </tbody>
        {isAssembly && form.items.length > 0 && (
          <tfoot>
            <tr style={{ borderTop: '2px solid var(--border)' }}>
              <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600 }}>
                Разом доля:
              </td>
              <td style={{ fontWeight: 600, color: pctOk ? 'inherit' : '#dc2626' }}
                  title={pctOk ? undefined : 'Сума долей має дорівнювати 100%'}>
                {total.toLocaleString('uk-UA', { maximumFractionDigits: 2 })}%
              </td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        )}
      </TchTable>
      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={new Date().toISOString().slice(0, 16)}
          catalogMode
          defaultOnlyWithStock
          onlyKind="goods"
          multiSelect
          onPick={(row) => addRowsFromPicker([row])}
          onPickMany={addRowsFromPicker}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {isAssembly && !pctOk && form.items.length > 0 && (
        <div style={{ marginTop: '0.5rem', color: '#dc2626', fontSize: '0.85rem' }}>
          Сума долей собівартості компонентів має дорівнювати 100% (зараз {total.toLocaleString('uk-UA', { maximumFractionDigits: 2 })}%).
        </div>
      )}

      </DocBlock>
      </DocLayout>
      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}
    </div>
  );
}
